import styles from '@/components/style/index.module.less';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logoIcon from '@/assets/images/translatorLogo.svg';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuthContext } from '@/hooks/auth';
import { Button, Input, message, Result, Space, Spin } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import css from './style.module.less';
import { getUserConfig, updateUserConfig } from '@/api/subtitles';
import { trackEvent, TrackingEvent } from '@/utils/tracking';
import { reportCustomLog } from '@/utils/logReporter';

function QRCodeComponent({
  url,
  ownerUid,
  onClose,
}: {
  url: string;
  ownerUid: string | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const copyQRCode = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      message.error(t('copyQRCodeFailed'));
      console.error('[sharelink] copy failed', err);
      return;
    }
    message.success(t('copyQRCodeSuccess'));

    trackEvent(TrackingEvent.SHARE_CREATE, {
      trigger_control: 'COPY_LINK',
      room_id: null,
      // PRD #4: sharelink 页只在主播会中打开, hardcode in_session
      url_type: 'in_session',
    });
    reportCustomLog('share_create', ownerUid, {
      trigger_control: 'COPY_LINK',
      room_id: null,
      url_type: 'in_session',
    });
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-gen') as HTMLCanvasElement | null;
    if (!canvas) {
      console.error('[sharelink] download failed: canvas not found');
      return;
    }
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = 'qr-code.png';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    trackEvent(TrackingEvent.SHARE_CREATE, {
      trigger_control: 'DOWNLOAD_QRCODE',
      room_id: null,
      url_type: 'in_session',
    });
    reportCustomLog('share_create', ownerUid, {
      trigger_control: 'DOWNLOAD_QRCODE',
      room_id: null,
      url_type: 'in_session',
    });
  };

  return (
    <Space direction="vertical" align="center">
      <QRCodeCanvas
        id="qr-gen"
        className={css.shareQRCode}
        value={url}
        size={256}
        level={'H'}
        imageSettings={{ // 可以在二维码中间添加图片
          src: logoIcon,
          height: 40,
          width: 40,
          excavate: true,
        }}
      />
      <Space direction="horizontal" size={10} align="center" >
        <Input value={url} disabled style={{ width: 300 }} />
        <Button type="primary" icon={<CopyOutlined />} onClick={copyQRCode}>
          {t('copyQRCode')}
        </Button>
        <Button type="primary" icon={<DownloadOutlined />} onClick={downloadQRCode}>
          {t('downloadQRCode')}
        </Button>
      </Space>
      <Button type="primary" danger onClick={onClose} style={{ marginTop: 20 }}>
        {t('liveShare.close')}
      </Button>
    </Space>
  );
}


const SHARE_KEY = 'USER_SUBTITLE_TRANSLATE_SHARE';

const App: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const { user } = state;
  const url = `${window.location.origin}/share/${user?.uid}`;
  const [isShare, setIsShare] = useState(false);
  const [checkShare, setCheckShare] = useState(false);
  // 添加loading状态
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getUserConfig(SHARE_KEY).then(res => {
      const share: any = res.data.data;
      console.log('share', share);
      setCheckShare(!!share[SHARE_KEY]);
      setIsShare(!!share[SHARE_KEY]);
      setLoading(false);
    });
  }, []);

  const liveShare = () => {
    updateUserConfig(SHARE_KEY, {
      value: true,
    }).then(_res => {
      setIsShare(true);
      setLoading(false);

      trackEvent(TrackingEvent.SHARE_CREATE, {
        trigger_control: 'TOGGLE_ON',
        room_id: null,
        url_type: 'in_session',
      });
      reportCustomLog('share_create', user?.uid, {
        trigger_control: 'TOGGLE_ON',
        room_id: null,
        url_type: 'in_session',
      });
    });
  };


  // 处理点击事件
  const handleShare = () => {
    setLoading(true);
    if (checkShare) {
      setTimeout(() => {
        setIsShare(true);
        setLoading(false);
      }, 1000);
    } else {
      liveShare();
    }
  };

  const handleClose = () => {
    updateUserConfig(SHARE_KEY, {
      value: false,
    }).then(_res => {
      setIsShare(false);
    });
  };

  if (loading) {
    return <div className={styles.pageContent}>
      <div className={styles.pageTitle}>{t('shareLink')}</div>
      <div className={css.shareQRCodeWrap}>
        <div className={css.loading}>
          <Spin />
        </div>
      </div>
    </div>;
  }

  return (
    <div className={styles.pageContent}>
      <div className={styles.pageTitle}>{t('shareLink')}</div>
      <div className={css.shareQRCodeWrap}>
        {!isShare && <Result
          title={t('liveShare.noShare')}
          extra={
            <Button 
              type="primary" 
              key="console" 
              onClick={handleShare}
            >
              {t('liveShare.open')}
            </Button>
          }
        />}
        {isShare && <>
          <QRCodeComponent url={url} ownerUid={user?.uid} onClose={handleClose}/>
        </>}
      </div>
    </div>
  );
};

      export default App;